import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { cn } from "@/shared/lib/cn";

type ToastKind = "info" | "success" | "error";

interface ToastItem {
  id: number;
  kind: ToastKind;
  message: string;
}

interface ToastContextValue {
  toast: (message: string, kind?: ToastKind) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

let seq = 0;

/** 全局轻提示，避免各页自造 alert */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);

  const toast = useCallback((message: string, kind: ToastKind = "info") => {
    const id = ++seq;
    // 截断异常长消息，避免把后端堆栈整页刷出
    const text = message.length > 240 ? `${message.slice(0, 240)}…` : message;
    setItems((prev) => [...prev.slice(-4), { id, kind, message: text }]);
    window.setTimeout(() => {
      setItems((prev) => prev.filter((t) => t.id !== id));
    }, 3200);
  }, []);

  const value = useMemo(() => ({ toast }), [toast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        className="pointer-events-none fixed bottom-4 right-4 z-50 flex w-[min(100vw-2rem,22rem)] flex-col gap-2"
        aria-live="polite"
      >
        {items.map((t) => (
          <div
            key={t.id}
            className={cn(
              "rounded-[var(--radius-md)] border px-3 py-2 text-[13px] shadow-[0_8px_24px_oklch(0_0_0/0.35)]",
              t.kind === "error" && "border-danger/40 bg-bg-elevated text-danger",
              t.kind === "success" &&
                "border-success/40 bg-bg-elevated text-success",
              t.kind === "info" && "border-border bg-bg-elevated text-fg",
            )}
          >
            {t.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within ToastProvider");
  return ctx;
}
